import {
    AdGroupSelection,
    CampaignSelection,
    PlacementSelection,
    PortfolioSelection,
} from './components/selectors/CampaignSelector'
import { DateOperator, type FilterI, type NumericFilter, Operator } from './constants/filter'
import type { SelectFilterOption } from './types/filter'
import { ActionsList, FrequenciesList, UpdateReportAdTypes } from './util/contstants'
import { recipeUpgradeListCopy } from './features/recipe-upgrades/list-copy'
import {
    UpdateStatuses as canonicalUpdateStatuses,
    updateStatusFilterOptions,
} from './constants/update_status'
import {
    deliveryReasonFilterOptions,
    deliveryStatusFilterOptions,
} from './constants/delivery_status'

export const DefaultNumericOperators = [
    Operator.GreaterThan,
    Operator.GreaterThanOrEqualTo,
    Operator.LessThan,
    Operator.LessThanOrEqualTo,
    Operator.EqualTo,
    Operator.NotEqualTo,
    Operator.IsNull,
    Operator.Between,
]

export const campaignFilter: NumericFilter = {
    operators: DefaultNumericOperators,
    key: 'campaigns',
    name: 'Campaigns',
    type: 'number',
}

export const activeCampaignFilter: NumericFilter = {
    operators: DefaultNumericOperators,
    key: 'active_campaigns',
    name: 'Enabled Campaigns',
    type: 'number',
}

export const adGroupFilter: NumericFilter = {
    operators: DefaultNumericOperators,
    key: 'ad_groups',
    name: 'Ad Groups',
    type: 'number',
}

export const activeAdGroupFilter: NumericFilter = {
    operators: DefaultNumericOperators,
    key: 'active_ad_groups',
    name: 'Enabled Ad Groups',
    type: 'number',
}

export const DefaultDateOperators = [
    DateOperator.GreaterThan,
    DateOperator.GreaterThanOrEqualTo,
    DateOperator.LessThan,
    DateOperator.LessThanOrEqualTo,
    DateOperator.EqualTo,
    DateOperator.InTheLast,
    DateOperator.IsNull,
    DateOperator.Between,
]

/*
 * Contains all of the constants
 */

export const AdType = {
    SponsoredProducts: 1,
    SponsoredBrands: 2,
}

export const adTypeString = (adTypeId: number): string => {
    switch (adTypeId) {
        case AdType.SponsoredProducts:
            return 'SP'
        case AdType.SponsoredBrands:
            return 'SB'
        default:
            console.warn('Cannot resolve ad type for', adTypeId)
            return ''
    }
}

export const adTypeStringFull = (adTypeId: number): string => {
    switch (adTypeId) {
        case AdType.SponsoredProducts:
            return 'Sponsored Products'
        case AdType.SponsoredBrands:
            return 'Sponsored Brands'
        default:
            console.warn('Cannot resolve ad type for', adTypeId)
            return ''
    }
}

export const TargetingType = {
    Manual: 1,
    Auto: 2,
}

export const TargetType = {
    Complements: 'Complements',
    Substitutes: 'Substitutes',
    Loose: 'Loose Match',
    Close: 'Close Match',
    Product: 'ASIN',
}

export const EventModes = {
    NORMAL: 1,
    PRETEND: 2,
    PENDING_APPROVAL: 3,
    APPROVED: 4,
    DENIED: 5,
}

export const EventParentTypes = {
    Profile: 0,
    CAMPAIGN: 1,
    AD_GROUP: 2,
    PRODUCT_AD: 3,
    KEYWORD: 4,
    TARGETS: 5,
    CampaignNegativeKeywords: 6,
    NegativeKeywords: 7,
    NegativeTargets: 8,
    Products: 9,
    Portfolios: 10,
    // 11 intentionally skipped
    CampaignPerformance: 12,
    KeywordPerformance: 13,
    KeywordTargetPerformance: 14,
    TargetPerformance: 15,

    SmartBid: 16,
    Promotion: 17,
    Recipe: 18,
    BulkAction: 19,
    SearchTerm: 20,
    Ads: 21,
    CampaignPlacements: 22,
    KeywordSearchTerms: 23,
    PublicApi: 24,

    Snapshots: 100,
    Reports: 101,
    Exports: 102,
}

export const MorphMaps = EventParentTypes

export const EventTypes = {
    INCREASE_BID_PERCENT: 1,
    INCREASE_BID_FLAT: 2,
    INCREASE_BUDGET_PERCENT: 3,
    INCREASE_BUDGET_FLAT: 4,
    PAUSE: 5,
    RESUME: 6,
}

export const UpdateStatuses = canonicalUpdateStatuses

export const updateReportAdTypeString = (adTypeId: number): string => {
    switch (adTypeId) {
        case UpdateReportAdTypes.Campaigns:
            return 'Campaigns'
        case UpdateReportAdTypes.AdGroups:
            return 'Ad Groups'
        case UpdateReportAdTypes.ProductAds:
            return 'Product Ads'
        case UpdateReportAdTypes.Keywords:
            return 'Keywords'
        case UpdateReportAdTypes.Targets:
            return 'Targets'
        case UpdateReportAdTypes.CampaignNegativeKeywords:
            return 'Campaign Negative Keywords'
        case UpdateReportAdTypes.NegativeKeywords:
            return 'Negative Keywords'
        case UpdateReportAdTypes.NegativeTargets:
            return 'Negative Targets'
        default:
            console.error('Cannot resolve report ad type for:', adTypeId)
            return ''
    }
}

export const UpdateSnapshotAdTypes = {
    Campaigns: 1,
    AdGroups: 2,
    Keywords: 3,
    NegativeKeyword: 4,
    CampaignNegativeKeywords: 5,
    ProductAds: 6,
    Targets: 7,
    NegativeTargets: 8,
    KeywordsAndTargets: 999,
}

export const updateSnapshotAdTypeString = (adTypeId: number): string => {
    switch (adTypeId) {
        case UpdateSnapshotAdTypes.Campaigns:
            return 'Campaigns'
        case UpdateSnapshotAdTypes.AdGroups:
            return 'Ad Groups'
        case UpdateSnapshotAdTypes.ProductAds:
            return 'Product Ads'
        case UpdateSnapshotAdTypes.Keywords:
            return 'Keywords'
        case UpdateSnapshotAdTypes.Targets:
            return 'Targets'
        case UpdateSnapshotAdTypes.CampaignNegativeKeywords:
            return 'Campaign Negative Keywords'
        case UpdateSnapshotAdTypes.NegativeKeyword:
            return 'Negative Keywords'
        case UpdateSnapshotAdTypes.NegativeTargets:
            return 'Negative Targets'
        default:
            console.error('Cannot updateSnapshotAdTypeString for:', adTypeId)
            return ''
    }
}

export const KeywordMatchTypes = {
    Exact: 1,
    Phrase: 2,
    Broad: 3,
}

export const resolveKeywordMatchType = (matchType: number): string => {
    switch (matchType) {
        case KeywordMatchTypes.Exact:
            return 'exact'
        case KeywordMatchTypes.Phrase:
            return 'phrase'
        case KeywordMatchTypes.Broad:
            return 'broad'
        default:
            console.warn('Cannot resolve keyword match type for', matchType)
            return ''
    }
}

const effectedEntityType: SelectFilterOption[] = [
    {
        value: EventParentTypes.CAMPAIGN,
        name: 'Campaign',
    },
    {
        value: EventParentTypes.AD_GROUP,
        name: 'Ad Group',
    },
    {
        value: EventParentTypes.PRODUCT_AD,
        name: 'Product Ad',
    },
    {
        value: EventParentTypes.KEYWORD,
        name: 'Keyword',
    },
    {
        value: EventParentTypes.TARGETS,
        name: 'Target',
    },
]

const auditLogEffectedEntityType: SelectFilterOption[] = [
    {
        value: EventParentTypes.CAMPAIGN,
        name: 'Campaign',
    },
    {
        value: EventParentTypes.CampaignNegativeKeywords,
        name: 'Campaign Negative Keyword',
    },
    {
        value: EventParentTypes.AD_GROUP,
        name: 'Ad Group',
    },
    {
        value: EventParentTypes.PRODUCT_AD,
        name: 'Product Ad',
    },
    {
        value: EventParentTypes.KEYWORD,
        name: 'Keyword',
    },
    {
        value: EventParentTypes.NegativeKeywords,
        name: 'Negative Keyword',
    },
    {
        value: EventParentTypes.TARGETS,
        name: 'Target',
    },
    {
        value: EventParentTypes.NegativeTargets,
        name: 'Negative Target',
    },
    {
        value: EventParentTypes.Promotion,
        name: 'Promotion',
    },
    {
        value: EventParentTypes.Recipe,
        name: 'Recipe',
    },
    {
        value: EventParentTypes.Portfolios,
        name: 'Portfolio',
    },
]

const auditLogSourceTypeOptions: SelectFilterOption[] = [
    {
        value: EventParentTypes.PublicApi,
        name: 'API',
    },
    {
        value: EventParentTypes.BulkAction,
        name: 'Bulk Action',
    },
    {
        value: EventParentTypes.Recipe,
        name: 'Recipe',
    },
    {
        value: EventParentTypes.SmartBid,
        name: 'Smart Bids',
    },
    // {
    //     value: EventParentTypes.Promotion,
    //     name: 'Promotion',
    // },
    // {
    //     value: EventParentTypes.SmartBid,
    //     name: 'Smart Bid',
    // },
]

const sourceTypeId: SelectFilterOption[] = [
    {
        value: EventParentTypes.SmartBid,
        name: 'Smart Bids',
    },
    {
        value: EventParentTypes.Promotion,
        name: 'Promotion',
    },
]

const statusOptions: SelectFilterOption[] = [
    {
        value: 1,
        name: 'Enabled',
    },
    {
        value: 2,
        name: 'Paused',
    },
    {
        value: 3,
        name: 'Archived',
    },
]

export const statusOptionsWithSplit: SelectFilterOption[] = [
    {
        value: 1,
        name: 'Enabled',
    },
    {
        value: 2,
        name: 'Paused',
    },
    {
        value: 3,
        name: 'Archived',
    },
    {
        value: -1,
        name: 'Split',
    },
]

const targetingOptions: SelectFilterOption[] = [
    {
        value: TargetingType.Manual,
        name: 'Manual',
    },
    {
        value: TargetingType.Auto,
        name: 'Auto',
    },
]

const keywordMatchTypeOptions: SelectFilterOption[] = [
    {
        value: KeywordMatchTypes.Exact,
        name: 'Exact',
    },
    {
        value: KeywordMatchTypes.Phrase,
        name: 'Phrase',
    },
    {
        value: KeywordMatchTypes.Broad,
        name: 'Broad',
    },
]

const expressionTypeOptions: SelectFilterOption[] = [
    {
        value: TargetingType.Manual,
        name: 'Manual',
    },
    {
        value: TargetingType.Auto,
        name: 'Auto',
    },
]

const targetTypeOptions: SelectFilterOption[] = [
    {
        value: TargetType.Close,
        name: 'Close Match',
    },
    {
        value: TargetType.Loose,
        name: 'Loose Match',
    },
    {
        value: TargetType.Substitutes,
        name: 'Substitutes',
    },
    {
        value: TargetType.Complements,
        name: 'Complements',
    },
    {
        value: TargetType.Product,
        name: 'ASIN',
    },
]

const updateStatusOptions: SelectFilterOption[] = updateStatusFilterOptions

const updateReportTypeOptions: SelectFilterOption[] = [
    {
        value: UpdateReportAdTypes.Campaigns,
        name: 'Campaigns',
    },
    {
        value: UpdateReportAdTypes.AdGroups,
        name: 'AdGroups',
    },
    {
        value: UpdateReportAdTypes.ProductAds,
        name: 'ProductAds',
    },
    {
        value: UpdateReportAdTypes.Keywords,
        name: 'Keywords',
    },
    {
        value: UpdateReportAdTypes.Targets,
        name: 'Targets',
    },
]

const smartBidOptions: SelectFilterOption[] = [
    {
        value: 1,
        name: 'Enabled',
    },
    {
        value: 0,
        name: 'Disabled',
    },
]

const recipeAdTypeOptions: SelectFilterOption[] = [
    {
        value: UpdateReportAdTypes.Campaigns,
        name: 'Campaigns',
    },
    {
        value: UpdateReportAdTypes.AdGroups,
        name: 'AdGroups',
    },
    {
        value: UpdateReportAdTypes.Keywords,
        name: 'Keywords',
    },
    {
        value: UpdateReportAdTypes.Targets,
        name: 'Targets',
    },
]

const recipeActionOptions: SelectFilterOption[] = [
    {
        value: ActionsList.SetState,
        name: 'Set State',
    },
    {
        value: ActionsList.SetDailyBudget,
        name: 'Set Daily Budget',
    },
    {
        value: ActionsList.SetDefaultBid,
        name: 'Set Default Bid',
    },
    {
        value: ActionsList.SetBid,
        name: 'Set Bid',
    },
]

const recipeFrequencyOptions: SelectFilterOption[] = [
    {
        name: 'After every data sync',
        value: FrequenciesList.AfterEveryDataSync,
    },
    {
        name: 'Once per Day',
        value: FrequenciesList.OncePerDay,
    },
    {
        name: 'Manually',
        value: FrequenciesList.Manually,
    },
    {
        name: 'On Certain Days',
        value: FrequenciesList.OnCertainDays,
    },
    {
        name: 'On Certain Dates',
        value: FrequenciesList.OnCertainDates,
    },
    {
        name: 'At a Certain Time',
        value: FrequenciesList.AtACertainTime,
    },
]

export const Filters: { [key: string]: FilterI } = {
    CampaignCount: {
        operators: DefaultNumericOperators,
        key: 'campaigns_count',
        name: 'Campaigns',
        type: 'number',
    },
    AdGroupCount: {
        operators: DefaultNumericOperators,
        key: 'ad_groups_count',
        name: 'Ad Groups',
        type: 'number',
    },
    PortfolioId: {
        key: 'portfolio_id',
        name: 'Portfolio',
        type: 'select-component',
        // @ts-expect-error
        component: PortfolioSelection,
    },
    CampaignId: {
        key: 'campaign_id',
        name: 'Campaign',
        type: 'select-component',
        // @ts-expect-error
        component: CampaignSelection,
    },
    AdGroupId: {
        key: 'ad_group_id',
        name: 'Ad Group',
        type: 'select-component',
        // @ts-expect-error
        component: AdGroupSelection,
    },
    PlacementId: {
        key: 'placement_id',
        name: 'Placement',
        type: 'select-component',
        // @ts-expect-error
        component: PlacementSelection,
    },
    Cost: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'cost',
        name: 'Spend',
        type: 'number',
    },
    ACOS: {
        suffix: '%',
        operators: DefaultNumericOperators,
        key: 'acos',
        name: 'ACOS',
        type: 'number',
    },
    ROAS: { operators: DefaultNumericOperators, key: 'roas', name: 'ROAS', type: 'number' },
    Sales: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'sales',
        name: 'Sales',
        type: 'number',
    },
    Clicks: { operators: DefaultNumericOperators, key: 'clicks', name: 'Clicks', type: 'number' },
    RPC: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'rpc',
        name: 'RPC',
        type: 'number',
    },
    CPC: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'cpc',
        name: 'CPC',
        type: 'number',
    },
    CTR: {
        suffix: '%',
        operators: DefaultNumericOperators,
        key: 'ctr',
        name: 'CTR',
        type: 'number',
    },
    Impressions: {
        operators: DefaultNumericOperators,
        key: 'impressions',
        name: 'Impressions',
        type: 'number',
    },
    Orders: { operators: DefaultNumericOperators, key: 'orders', name: 'Orders', type: 'number' },
    CVR: {
        suffix: '%',
        operators: DefaultNumericOperators,
        key: 'cvr',
        name: 'CVR',
        type: 'number',
    },
    CAC: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'cac',
        name: 'CAC',
        type: 'number',
    },
    AOV: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'aov',
        name: 'AOV',
        type: 'number',
    },
    TargetCPC: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'ideal_cpc',
        name: 'Target CPC',
        type: 'number',
    },
    TargetACOS: {
        suffix: '%',
        operators: DefaultNumericOperators,
        key: 'target_acos',
        name: 'Target ACOS',
        type: 'number',
    },
    DailyBudget: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'daily_budget',
        name: 'Budget',
        type: 'number',
    },

    State: { key: 'state', name: 'State', type: 'select', options: statusOptions },
    DeliveryStatus: {
        key: 'delivery_status',
        name: 'Delivery Status',
        type: 'select',
        options: deliveryStatusFilterOptions,
    },
    DeliveryReason: {
        key: 'delivery_reason',
        name: 'Delivery Reason',
        type: 'select',
        options: deliveryReasonFilterOptions,
    },
    CustomFieldEntityType: {
        key: 'entity_type',
        name: 'Entity type',
        type: 'select',
        options: [
            { value: 'campaign', name: 'Campaigns' },
            { value: 'ad_group', name: 'Ad groups' },
            { value: 'target', name: 'Keywords & targets' },
            { value: 'ad', name: 'Ads' },
        ],
    },
    CustomFieldDataType: {
        key: 'data_type',
        name: 'Type',
        type: 'select',
        options: [
            { value: 1, name: 'Number' },
            { value: 2, name: 'Boolean' },
            { value: 3, name: 'String' },
        ],
    },
    StateWithSplit: {
        key: 'state',
        name: 'State',
        type: 'select',
        options: statusOptionsWithSplit,
    },
    TargetingType: {
        key: 'targeting_type',
        name: 'Targeting',
        type: 'select',
        options: targetingOptions,
    },
    KeywordMatchType: {
        key: 'match_type',
        name: 'Targeting',
        type: 'select',
        options: keywordMatchTypeOptions,
    },
    DefaultBid: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'default_bid',
        name: 'Bid',
        type: 'number',
    },
    Bid: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'bid',
        name: 'Bid',
        type: 'number',
    },
    ExpressionType: {
        key: 'expression_type',
        name: 'Targeting',
        type: 'select',
        options: expressionTypeOptions,
    },
    TargetingExpression: {
        key: 'targeting_expression',
        name: 'Targeting Expression',
        type: 'select',
        options: targetTypeOptions,
    },

    UpdateStatus: {
        key: 'status_id',
        name: 'Status',
        type: 'select',
        options: updateStatusOptions,
    },
    UpdateReportType: {
        key: 'type',
        name: 'Type',
        type: 'select',
        options: updateReportTypeOptions,
    },

    EffectedEntityId: {
        key: 'target_id',
        operators: [Operator.EqualTo],
        name: 'Effected Entity Id',
        type: 'number',
    },
    EffectedEntityType: {
        key: 'target_type_id',
        name: 'Effected Entity Type',
        type: 'select',
        options: effectedEntityType,
    },
    SourceTypeId: {
        key: 'source_type_id',
        name: 'Source Type',
        type: 'select',
        options: sourceTypeId,
    },
    SourceId: {
        operators: [Operator.EqualTo],
        key: 'source_id',
        name: 'Source Id',
        type: 'number',
    },
    SmartBid: { key: 'smart_bid', name: 'Smart Bid', type: 'select', options: smartBidOptions },

    PagesRead: {
        operators: DefaultNumericOperators,
        key: 'pages_read',
        name: 'Pages Read',
        type: 'number',
    },
    EstimatedRoyalties: {
        prefix: window.marketplace.currency_symbol,
        operators: DefaultNumericOperators,
        key: 'estimated_royalties',
        name: 'Estimated Royalties',
        type: 'number',
    },

    RecipeEnabled: { key: 'enabled', name: 'Enabled', type: 'select', options: smartBidOptions },
    RecipeAdTypes: {
        key: 'ad_type',
        name: 'Ad Type',
        type: 'select',
        options: recipeAdTypeOptions,
    },
    RecipeActions: { key: 'action', name: 'Action', type: 'select', options: recipeActionOptions },
    RecipeFrequencies: {
        key: 'frequency',
        name: 'Frequency',
        type: 'select',
        options: recipeFrequencyOptions,
    },
    RecipeUpgradeStatus: {
        key: 'upgrade_status',
        name: 'Upgrade Status',
        type: 'select',
        options: [
            { value: 'not_upgraded', name: recipeUpgradeListCopy.notYetUpgraded },
            { value: 'upgraded', name: recipeUpgradeListCopy.upgraded },
        ],
    },

    StartDate: {
        operators: DefaultDateOperators,
        key: 'start_date',
        name: 'Start Date',
        type: 'date',
    },
    EndDate: {
        operators: DefaultDateOperators,
        key: 'end_date',
        name: 'End Date',
        type: 'date',
    },
    AuditLogEffectedEntityType: {
        key: 'target_type_id',
        name: 'Effected Entity Type',
        type: 'select',
        options: auditLogEffectedEntityType,
    },
    AuditLogSourceType: {
        key: 'source_type_id',
        name: 'Source Type',
        type: 'select',
        options: auditLogSourceTypeOptions,
    },
    AuditLogEffectedEntities: {
        operators: DefaultNumericOperators,
        key: 'meta->count',
        name: 'Effected Entities',
        type: 'number',
    },
}

export const HasPromotionFilter: FilterI = {
    key: 'has_promotions',
    name: 'Has Smart Campaigns',
    type: 'select',
    options: [
        { value: 0, name: 'False' },
        { value: 1, name: 'True' },
    ],
}

export const PerformanceFilters: FilterI[] = [
    Filters.Cost,
    Filters.Sales,
    Filters.ACOS,
    Filters.ROAS,
    Filters.Impressions,
    Filters.Clicks,
    Filters.CTR,
    Filters.RPC,
    Filters.CPC,
    Filters.Orders,
    Filters.CVR,
    Filters.CAC,
    Filters.AOV,
]

export const KDPPerformanceFilters: FilterI[] = [Filters.PagesRead, Filters.EstimatedRoyalties]

export const TableName = {
    Campaign: 'Campaigns',
    CampaignPlacement: 'Placements',
    AdGroup: 'Ad Groups',
    Target: 'Targets',
    NegativeTarget: 'Negative Targets',
    Keyword: 'Keywords',
    NegativeKeyword: 'Negative Keywords',
    Product: 'Products',
    ProductAds: 'Product Ads',
    Portfolio: 'Portfolios',
    CampaignNegativeKeyword: 'Campaign Negative Keywords',
    SearchTerm: 'Search Terms',
    Promotions: 'Smart Campaigns',
    Recipes: 'Recipes',
    PublicRecipes: 'Public Recipes',
    Segments: 'Segments',
    Logs: 'Logs',
    CustomFields: 'Custom Fields',
}

export const defaultFilter = {
    filter: Filters.StateWithSplit,
    value: statusOptionsWithSplit[0].value,
}

export const RecipeTrigger = {
    Manual: 1,
    Auto: 2,
}

export const BulkAction = {
    ChangeState: 1,
    ChangeDefaultBid: 2,
    ChangeDefaultBidPercent: 3,
    ChangeDailyBudget: 4,
    ChangeDailyBudgetPercent: 5,
    ChangeBid: 6,
    ChangeBidPercent: 7,
    ChangeStartDate: 8,
    ChangeEndDate: 9,
    ChangeName: 10,
    ChangePremiumBidAdjustment: 11,
    ChangeTacos: 12,
    ChangeAssignedPortfolio: 13,
    DeletePortfolio: 14,
    CreateNegativeKeyword: 15,
    ChangeDefaultBidDelta: 16,
    ChangeDailyBudgetDelta: 17,
    ChangeBidDelta: 18,
    ChangeSmartBid: 19,
    AddAsinToAdGroup: 20,
    ChangeBidStrategy: 21,
    ChangeTosPlacement: 22,
    ChangeRosPlacement: 23,
    ChangePpPlacement: 24,
}
